/**
 * Flow collections: `[a, b]` and `{a: 1}`, including quoted scalars, anchors, aliases,
 * comments and line breaks inside them.
 */

import {
  DataError,
  type DataMap,
  type DataValue,
} from "@/lib/tool-kit/latex-dev/data-model";
import { type Line, parseProps } from "@/lib/tool-kit/latex-dev/yaml-lines";
import { keyToString } from "@/lib/tool-kit/latex-dev/yaml-scalar";
import {
  type Fail,
  quotedEnd,
  readDoubleQuoted,
  readSingleQuoted,
} from "@/lib/tool-kit/latex-dev/yaml-text";

/** What the flow reader needs from the document parser. */
export interface FlowHost {
  lookupAnchor(name: string): DataValue | undefined;
  makeScalar(
    text: string,
    quoted: boolean,
    tag: string | null,
    line: Line
  ): DataValue;
  applyProps(
    tag: string | null,
    anchor: string | null,
    value: DataValue,
    line: Line,
    col: number,
    collection: boolean
  ): void;
}

type Params = Record<string, string | number>;

const RE_ALIAS = /^\*[^\s,[\]{}]+/;
const RE_FOLD = /[ \t]*\n[ \t]*/g;
const FLOW_SPACE = " \t\n";
const FLOW_STOPS = ",[]{}";

/** A quote starts a quoted scalar only where a value or key can start. */
function startsValue(text: string, i: number): boolean {
  let j = i - 1;
  while (j >= 0 && FLOW_SPACE.includes(text[j])) {
    j--;
  }
  return j < 0 || ",[{:".includes(text[j]);
}

/**
 * The index just past a quoted scalar or comment that starts at `i`; `i` itself when
 * neither does, and -1 for a quote that never closes.
 */
function skipOpaque(text: string, i: number): number {
  const c = text[i];
  if ((c === '"' || c === "'") && startsValue(text, i)) {
    return quotedEnd(text, i);
  }
  if (c === "#" && (i === 0 || FLOW_SPACE.includes(text[i - 1]))) {
    const newline = text.indexOf("\n", i);
    return newline < 0 ? text.length : newline;
  }
  return i;
}

/** Does the flow collection that starts the text close inside it? */
export function flowComplete(text: string): boolean {
  let depth = 0;
  let i = 0;
  while (i < text.length) {
    const skipped = skipOpaque(text, i);
    if (skipped < 0) {
      return false;
    }
    if (skipped > i) {
      i = skipped;
      continue;
    }
    const c = text[i];
    if (c === "[" || c === "{") {
      depth++;
    } else if ((c === "]" || c === "}") && --depth === 0) {
      return true;
    }
    i++;
  }
  return depth === 0 && text.trim() !== "";
}

export class FlowReader {
  private i = 0;
  private readonly text: string;
  private readonly line: Line;
  private readonly col: number;
  private readonly host: FlowHost;

  constructor(text: string, line: Line, col: number, host: FlowHost) {
    this.text = text;
    this.line = line;
    this.col = col;
    this.host = host;
  }

  private fail(code: string, at: number, params: Params = {}): never {
    const rows = this.text.slice(0, at).split("\n");
    const last = rows.length - 1;
    const column = last === 0 ? this.col + at : rows[last].length + 1;
    throw new DataError(code, this.line.no + last, column, params);
  }

  private failer(): Fail {
    return (code, offset, params = {}) => this.fail(code, offset, params);
  }

  parseDocument(): DataValue {
    const value = this.node();
    this.skip();
    if (this.i < this.text.length) {
      this.fail("unexpectedContent", this.i);
    }
    return value;
  }

  private skip(): void {
    for (;;) {
      const c = this.text[this.i];
      if (c === " " || c === "\t" || c === "\n") {
        this.i++;
      } else if (
        c === "#" &&
        (this.i === 0 || FLOW_SPACE.includes(this.text[this.i - 1]))
      ) {
        const newline = this.text.indexOf("\n", this.i);
        this.i = newline < 0 ? this.text.length : newline;
      } else {
        return;
      }
    }
  }

  private node(): DataValue {
    this.skip();
    const props = parseProps(this.text.slice(this.i));
    this.i += props.used;
    const c = this.text[this.i];
    const collection = c === "[" || c === "{";
    let value: DataValue;
    if (c === "[") {
      value = this.sequence();
    } else if (c === "{") {
      value = this.mapping();
    } else if (c === '"' || c === "'") {
      value = this.quoted(props.tag);
    } else if (c === "*") {
      value = this.alias();
    } else {
      value = this.plain(props.tag);
    }
    this.host.applyProps(
      props.tag,
      props.anchor,
      value,
      this.line,
      this.col,
      collection
    );
    return value;
  }

  private alias(): DataValue {
    const match = RE_ALIAS.exec(this.text.slice(this.i));
    if (!match) {
      return this.fail("badCharacter", this.i, { char: "*" });
    }
    const name = match[0].slice(1);
    const value = this.host.lookupAnchor(name);
    if (value === undefined) {
      this.fail("unknownAlias", this.i, { name });
    }
    this.i += match[0].length;
    return value as DataValue;
  }

  private quoted(tag: string | null): DataValue {
    const start = this.i;
    const result =
      this.text[start] === '"'
        ? readDoubleQuoted(this.text, start, this.failer())
        : readSingleQuoted(this.text, start);
    if (!result) {
      return this.fail("unterminatedQuote", start);
    }
    this.i = result.end;
    return this.host.makeScalar(result.value, true, tag, this.line);
  }

  private plainEnd(start: number): number {
    let end = start;
    while (end < this.text.length) {
      const c = this.text[end];
      const next = this.text[end + 1];
      const colon =
        c === ":" &&
        (next === undefined ||
          FLOW_SPACE.includes(next) ||
          FLOW_STOPS.includes(next));
      const comment =
        c === "#" && end > start && FLOW_SPACE.includes(this.text[end - 1]);
      if (FLOW_STOPS.includes(c) || colon || comment) {
        break;
      }
      end++;
    }
    return end;
  }

  private plain(tag: string | null): DataValue {
    const start = this.i;
    const end = this.plainEnd(start);
    this.i = end;
    const text = this.text.slice(start, end).replace(RE_FOLD, " ").trim();
    if (text === "" && tag === null) {
      const next = this.text[this.i];
      if (next === "," || next === "]" || next === "}" || next === ":") {
        return null;
      }
      return this.fail("flowExpected", this.i, { expected: "value" });
    }
    return this.host.makeScalar(text, false, tag, this.line);
  }

  private sequence(): DataValue[] {
    this.i++;
    const items: DataValue[] = [];
    for (;;) {
      this.skip();
      const c = this.text[this.i];
      if (c === "]") {
        this.i++;
        return items;
      }
      if (c === undefined) {
        return this.fail("unterminatedFlow", this.i);
      }
      if (c === ",") {
        return this.fail("flowExpected", this.i, { expected: "value" });
      }
      items.push(this.sequenceEntry());
      this.skip();
      if (this.text[this.i] === ",") {
        this.i++;
      } else if (this.text[this.i] !== "]") {
        this.fail("flowExpected", this.i, { expected: ", ]" });
      }
    }
  }

  /** An entry of a sequence: a value, or a one-pair mapping written `key: value`. */
  private sequenceEntry(): DataValue {
    const at = this.i;
    const key = this.node();
    this.skip();
    if (this.text[this.i] !== ":") {
      return key;
    }
    this.i++;
    this.skip();
    const next = this.text[this.i];
    const value = next === "," || next === "]" ? null : this.node();
    const pair: DataMap = new Map();
    this.setKey(pair, key, value, at);
    return pair;
  }

  private mapping(): DataMap {
    this.i++;
    const map: DataMap = new Map();
    for (;;) {
      this.skip();
      const c = this.text[this.i];
      if (c === "}") {
        this.i++;
        return map;
      }
      if (c === undefined) {
        return this.fail("unterminatedFlow", this.i);
      }
      if (c === ",") {
        return this.fail("flowExpected", this.i, { expected: "key" });
      }
      this.mappingEntry(map);
      this.skip();
      if (this.text[this.i] === ",") {
        this.i++;
      } else if (this.text[this.i] !== "}") {
        this.fail("flowExpected", this.i, { expected: ", }" });
      }
    }
  }

  private mappingEntry(map: DataMap): void {
    const at = this.i;
    const key = this.text[this.i] === ":" ? null : this.node();
    this.skip();
    let value: DataValue = null;
    if (this.text[this.i] === ":") {
      this.i++;
      this.skip();
      const next = this.text[this.i];
      value = next === "," || next === "}" ? null : this.node();
    }
    this.setKey(map, key, value, at);
  }

  private setKey(
    map: DataMap,
    key: DataValue,
    value: DataValue,
    at: number
  ): void {
    const text = keyToString(key);
    if (text === null) {
      this.fail("complexKey", at);
    }
    const name = text as string;
    if (map.has(name)) {
      this.fail("duplicateKey", at, { key: name, first: this.line.no });
    }
    map.set(name, value);
  }
}
